import { listCohortOrgs } from './cohortApi.js'
import { reconcileSelectedCohort, selectedCohortState } from './cohortSelection.js'
import { shallowRef, watch } from 'vue'
import { authToken, researchIdentity } from './researchPermissions.js'
import { createCohortDiscovery } from './cohortDiscoveryAuthority.js'

export const cohortDirectoryState = shallowRef(null)
let directoryToken = null
let directoryRequest = null
let directoryRevision = 0
let directoryLoadedAt = 0
let selectionToken = authToken()

export async function loadCohortDirectory({ force = false } = {}) {
  const token = authToken()
  if (directoryToken !== token) {
    directoryRevision++
    directoryToken = token
    cohortDirectoryState.value = null
    directoryRequest = null
  }
  if (force) {
    cohortDirectoryState.value = null
  }
  if (!force && directoryRequest) return directoryRequest
  if (!force && cohortDirectoryState.value !== null && Date.now() - directoryLoadedAt < 60000) return cohortDirectoryState.value
  const revision = ++directoryRevision
  const request = listCohortOrgs().then(nodes => {
    if (revision !== directoryRevision || token !== authToken()) throw new Error('Cohort identity changed')
    if (!Array.isArray(nodes)) throw new Error('Invalid cohort directory')
    directoryLoadedAt = Date.now()
    cohortDirectoryState.value = nodes
    return nodes
  })
  directoryRequest = request
  try { return await request } finally { if (directoryRequest === request) directoryRequest = null }
}

// Reuse authenticated discovery for up to 60 seconds; selector entry refreshes it.
// A realtime denial invalidates completed results before forced confirmation.
const cohortDiscovery = createCohortDiscovery({
  listOrgs: options => loadCohortDirectory(options),
  getSelection: () => selectedCohortState.value,
  reconcile: reconcileSelectedCohort
})

export function refreshCohortSelection(options) {
  syncDiscoveryIdentity()
  return cohortDiscovery.refresh(options)
}

export function invalidateCohortDiscovery() {
  directoryRevision++
  cohortDirectoryState.value = null
  directoryRequest = null
  cohortDiscovery.invalidate()
}

export async function confirmCohortRealtimeRevocation(selectedId) {
  syncDiscoveryIdentity()
  invalidateCohortDiscovery()
  try { return await cohortDiscovery.confirmRealtimeRevocation(selectedId) }
  finally { invalidateCohortDiscovery() }
}

function syncDiscoveryIdentity() {
  const token = authToken()
  if (selectionToken === token) return
  selectionToken = token
  invalidateCohortDiscovery()
  // Keep the selected org/site as navigation intent, but revoke old capabilities.
  if (selectedCohortState.value) selectedCohortState.value = {
    ...selectedCohortState.value, realtime_monitoring_enabled: false
  }
}
watch(() => researchIdentity.value.token, () => {
  invalidateCohortDiscovery()
  syncDiscoveryIdentity()
  if (selectedCohortState.value) selectedCohortState.value = {
    ...selectedCohortState.value, realtime_monitoring_enabled: false
  }
}, { flush: 'sync' })
